/*
 * Newsletter — one subscription flow shared by the welcome popup
 * (sections/popups.liquid) and the footer newsletter column
 * (sections/footer.liquid).
 *
 * - submit(form): posts the native Shopify customer form in the background.
 *   Shopify answers a successful post with a redirect to ?customer_posted=true;
 *   when its bot protection kicks in it redirects to /challenge instead — then
 *   the form is submitted natively so the shopper can complete the captcha
 *   (the old fetch treated the challenge page as "success" and nothing was saved).
 * - A subscribed shopper is remembered (localStorage + customer.accepts_marketing,
 *   which theme.liquid exposes as <html data-newsletter-subscribed> +
 *   class="is-newsletter-subscribed") so the footer shows the "ya estás
 *   suscrito" state with the discount code and the popup stops appearing.
 * - createStepper(): the shared "email + preference → WhatsApp → confirmation"
 *   3-step flow engine. Both the welcome popup and the footer newsletter
 *   column build one of these over their own markup — same validation, tag
 *   handling and submit logic, different presentation.
 * - [data-copy-code] buttons copy their code to the clipboard.
 */
(function () {
  var STORAGE_KEY = 'welcome_popup_subscribed';
  var root = document.documentElement;

  function remember() {
    try { window.localStorage.setItem(STORAGE_KEY, 'true'); } catch (e) { /* private mode */ }
  }

  function isSubscribed() {
    if (root.getAttribute('data-newsletter-subscribed') === 'true') return true;
    try { return window.localStorage.getItem(STORAGE_KEY) === 'true'; } catch (e) { return false; }
  }

  function markSubscribed() {
    remember();
    root.classList.add('is-newsletter-subscribed');
    document.dispatchEvent(new CustomEvent('newsletter:subscribed'));
  }

  function errorFromResponse(html, formId) {
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var form = formId && doc.getElementById(formId);
      var scope = form || doc;
      var el = scope.querySelector('.errors, [data-newsletter-email-error]:not([hidden]), .form__message');
      return el ? el.textContent.trim() : '';
    } catch (e) {
      return '';
    }
  }

  /** Resolves {ok: true} | {ok: false, message} | {redirected: true} (native submit). */
  function submit(form) {
    var data = new FormData(form);
    return fetch(form.getAttribute('action') || '/contact', {
      method: 'POST',
      body: data,
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    })
      .then(function (res) {
        var url = res.url || '';
        // Protección anti-bots de Shopify: a veces redirige a /challenge y a
        // veces responde 403/429 con "Verifying your connection..." (sin
        // redirect). En ambos casos el POST por fetch no crea el cliente, así
        // que se hace el envío nativo para que Shopify muestre su verificación;
        // al volver, popup/footer retoman el paso (form.posted_successfully).
        if (url.indexOf('/challenge') !== -1) {
          HTMLFormElement.prototype.submit.call(form);
          return { redirected: true };
        }
        // Shopify solo redirige DESPUÉS de procesar el POST (cliente creado /
        // actualizado). El GET de esa redirección a veces lo bloquea la
        // protección anti-bots con 403, pero la suscripción ya quedó hecha:
        // por eso esto va ANTES del chequeo de 403 (antes se mostraba un falso
        // "correo inválido" con el cliente ya creado).
        if (url.indexOf('customer_posted=true') !== -1 || res.redirected) {
          markSubscribed();
          return { ok: true };
        }
        if (res.status === 403 || res.status === 429) {
          HTMLFormElement.prototype.submit.call(form);
          return { redirected: true };
        }
        return res.text().then(function (html) {
          var message = errorFromResponse(html, form.id);
          // Shopify re-renders the page with the form errors; no error markup
          // means the post went through (e.g. the email was already subscribed).
          if (!message && res.ok) {
            markSubscribed();
            return { ok: true };
          }
          // Respuesta no OK sin un error de formulario concreto (p. ej. otra
          // página intermedia de Shopify): envío nativo en vez de mostrar un
          // falso "correo inválido".
          if (!message) {
            HTMLFormElement.prototype.submit.call(form);
            return { redirected: true };
          }
          return { ok: false, message: message };
        });
      })
      .catch(function () {
        return { ok: false, message: '' };
      });
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-copy-code]');
    if (!btn) return;
    var code = btn.getAttribute('data-copy-code');
    var done = function () {
      var label = btn.getAttribute('data-copied-label');
      if (!label) return;
      var original = btn.textContent;
      btn.textContent = label;
      setTimeout(function () { btn.textContent = original; }, 2000);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done, done);
    } else {
      var tmp = document.createElement('textarea');
      tmp.value = code;
      document.body.appendChild(tmp);
      tmp.select();
      try { document.execCommand('copy'); } catch (e) { /* no-op */ }
      tmp.remove();
      done();
    }
  });

  if (isSubscribed()) root.classList.add('is-newsletter-subscribed', 'is-newsletter-subscribed-onload');

  /**
   * Shared 3-step signup engine: 1) email + preference  2) WhatsApp
   * (optional, can be skipped)  3) confirmation. Callers keep their own
   * markup/CSS/copy and just pass element references; this only knows the
   * flow — step switching, validation, tag merging and posting via submit()
   * above.
   *
   * @param {Object} o
   * @param {Element[]} o.steps - the 3 step wrappers, in DOM order.
   * @param {Function} [o.setStepActive] - (stepEl, isActive) => void.
   *   Defaults to toggling `hidden` + the `is-active` class (welcome popup).
   * @param {Element} [o.statusEl] - aria-live region, filled from o.stepLabels.
   * @param {string[]} [o.stepLabels]
   * @param {Element} [o.doneTextEl] - step-3 text node with
   *   data-text-whatsapp / data-text-email attributes (optional).
   * @param {Object} [o.step1] - { form, emailInput, honeypotInput, errorEl,
   *   submitBtn, tagsInput, preferenceInputs, onSuccess(email) }
   * @param {Object} [o.step2] - { form, countrySelect, phoneInput, errorEl,
   *   submitBtn, tagsInput, emailInput, skipBtn, countries, defaultCountry }
   * @param {Function} [o.onFinish] - (variant) => void
   * @returns {{goToStep, finish, getEmail}}
   */
  function createStepper(o) {
    var current = 1;
    var submittedEmail = '';
    // Email validado en el paso 1 que todavía no se envió a Shopify. El
    // formulario de cliente de Shopify NO modifica un cliente que ya existe
    // (un POST anónimo no puede cambiar datos de otra cuenta), así que si el
    // paso 1 creaba el cliente, el paso 2 ya no podía guardarle el WhatsApp.
    // Por eso se envía UNA sola vez: en el paso 2 (email + tags + WhatsApp),
    // o con "Ahora no", o al cerrar/salir (flushPending) para no perder la
    // suscripción.
    var pendingStep1 = false;

    function flushPending() {
      if (!pendingStep1 || !o.step1 || !o.step1.form) return;
      pendingStep1 = false;
      try {
        var fd = new FormData(o.step1.form);
        if (navigator.sendBeacon && navigator.sendBeacon(o.step1.form.getAttribute('action') || '/contact', fd)) {
          markSubscribed();
          return;
        }
      } catch (e) { /* cae al fetch */ }
      submit(o.step1.form);
    }
    window.addEventListener('pagehide', flushPending);

    function defaultSetActive(stepEl, isActive) {
      stepEl.hidden = !isActive;
      stepEl.classList.toggle('is-active', isActive);
    }
    var setStepActive = o.setStepActive || defaultSetActive;

    function focusFirst(stepEl) {
      if (!stepEl) return;
      var el = stepEl.querySelector('input:not([tabindex="-1"]), select, button, a[href]');
      if (el) el.focus({ preventScroll: true });
    }

    function goToStep(n, opts) {
      opts = opts || {};
      current = n;
      o.steps.forEach(function (stepEl, i) { setStepActive(stepEl, i + 1 === n); });
      if (opts.silent) return;
      if (o.statusEl) o.statusEl.textContent = (o.stepLabels && o.stepLabels[n - 1]) || '';
      focusFirst(o.steps[n - 1]);
    }

    function finish(variant) {
      if (o.doneTextEl) {
        var text = variant === 'whatsapp'
          ? o.doneTextEl.getAttribute('data-text-whatsapp')
          : o.doneTextEl.getAttribute('data-text-email');
        if (text) o.doneTextEl.textContent = text;
      }
      goToStep(3);
      if (o.onFinish) o.onFinish(variant);
    }

    // ── Step 1: email + preference ────────────────────────────────
    if (o.step1 && o.step1.form) {
      (function () {
        var s = o.step1;
        var form = s.form, email = s.emailInput, honeypot = s.honeypotInput,
          error = s.errorEl, submitBtn = s.submitBtn, tagsInput = s.tagsInput;
        var baseTags = tagsInput ? tagsInput.value : 'newsletter';

        form.addEventListener('submit', function (e) {
          email.value = email.value.trim();
          if (!email.value || !email.checkValidity()) {
            e.preventDefault();
            if (error) { error.textContent = error.dataset.message; error.hidden = false; }
            email.focus();
            return;
          }
          if (error) error.hidden = true;

          if (tagsInput) {
            var checked = s.preferenceInputs && Array.prototype.filter.call(s.preferenceInputs, function (r) { return r.checked; })[0];
            tagsInput.value = checked ? baseTags + ',genero:' + checked.value : baseTags;
          }
          submittedEmail = email.value;

          // Honeypot filled in: only a bot does that. Fake success, post nothing.
          if (honeypot && honeypot.value) {
            e.preventDefault();
            finish('email');
            return;
          }

          if (!window.AldoNewsletter) return; // native POST (no JS)
          e.preventDefault();
          if (o.step2 && o.step2.form) {
            // Se envía junto con el WhatsApp en el paso 2 (ver pendingStep1).
            pendingStep1 = true;
            if (o.step2.emailInput) o.step2.emailInput.value = submittedEmail;
            if (s.onSuccess) s.onSuccess(submittedEmail);
            goToStep(2);
            return;
          }
          var label = submitBtn ? submitBtn.textContent : '';
          if (submitBtn) {
            submitBtn.setAttribute('aria-busy', 'true');
            submitBtn.textContent = submitBtn.dataset.loadingLabel || label;
          }
          submit(form).then(function (result) {
            if (result.redirected) return;
            if (submitBtn) { submitBtn.removeAttribute('aria-busy'); submitBtn.textContent = label; }
            if (result.ok) {
              if (o.step2 && o.step2.emailInput) o.step2.emailInput.value = submittedEmail;
              if (s.onSuccess) s.onSuccess(submittedEmail);
              goToStep(2);
            } else if (error) {
              error.textContent = result.message || error.dataset.message;
              error.hidden = false;
            }
          });
        });
      })();
    }

    // ── Step 2: WhatsApp ────────────────────────────────────────────
    if (o.step2 && o.step2.form) {
      (function () {
        var s = o.step2;
        var countries = s.countries || [];
        var defaultCountry = s.defaultCountry || (countries[0] && countries[0].iso);

        if (s.countrySelect && !s.countrySelect.options.length) {
          countries.forEach(function (c) {
            var opt = document.createElement('option');
            opt.value = c.iso;
            opt.textContent = c.flag + ' +' + c.dial;
            opt.setAttribute('aria-label', c.name + ' +' + c.dial);
            if (c.iso === defaultCountry) opt.selected = true;
            s.countrySelect.appendChild(opt);
          });
          if (!s.countrySelect.value && countries.length) s.countrySelect.value = countries[0].iso;
        }

        function currentCountry() {
          var iso = s.countrySelect ? s.countrySelect.value : '';
          for (var i = 0; i < countries.length; i++) {
            if (countries[i].iso === iso) return countries[i];
          }
          return countries[0] || { dial: '', min: 0, max: 99 };
        }

        if (s.skipBtn) s.skipBtn.addEventListener('click', function () {
          // "Ahora no": se suscribe solo con el email del paso 1.
          if (pendingStep1 && o.step1 && o.step1.form) {
            pendingStep1 = false;
            submit(o.step1.form).then(function (result) {
              if (!result.redirected) finish('email');
            });
            return;
          }
          finish('email');
        });

        s.form.addEventListener('submit', function (e) {
          e.preventDefault();
          if (s.errorEl) s.errorEl.hidden = true;
          var country = currentCountry();
          var digits = (s.phoneInput.value || '').replace(/\D/g, '').replace(/^0+/, '');
          if (!digits || digits.length < country.min || digits.length > country.max) {
            if (s.errorEl) { s.errorEl.textContent = s.errorEl.dataset.message; s.errorEl.hidden = false; }
            s.phoneInput.focus();
            return;
          }
          var e164 = '+' + country.dial + digits;
          // Se reenvían también los tags del paso 1 (newsletter, genero:*): si
          // Shopify reemplaza la lista de tags en vez de sumarla al volver a
          // enviar el mismo email, así no se pierde la preferencia.
          if (s.tagsInput) {
            var step1Tags = (o.step1 && o.step1.tagsInput && o.step1.tagsInput.value) || 'newsletter';
            var tagList = step1Tags.split(',');
            tagList.push('whatsapp_optin', 'whatsapp:' + e164);
            s.tagsInput.value = tagList.filter(function (t, i, a) { return t && a.indexOf(t) === i; }).join(',');
          }

          if (!window.AldoNewsletter) {
            HTMLFormElement.prototype.submit.call(s.form);
            return;
          }
          var label = s.submitBtn ? s.submitBtn.textContent : '';
          if (s.submitBtn) {
            s.submitBtn.setAttribute('aria-busy', 'true');
            s.submitBtn.textContent = s.submitBtn.dataset.loadingLabel || label;
          }
          pendingStep1 = false; // este envío ya lleva el email + los tags del paso 1
          submit(s.form).then(function (result) {
            if (result.redirected) return;
            if (s.submitBtn) { s.submitBtn.removeAttribute('aria-busy'); s.submitBtn.textContent = label; }
            if (result.ok) {
              pendingStep1 = false;
              finish('whatsapp');
            } else if (s.errorEl) {
              s.errorEl.textContent = result.message || s.errorEl.dataset.message;
              s.errorEl.hidden = false;
            }
          });
        });
      })();
    }

    return { goToStep: goToStep, finish: finish, flushPending: flushPending, getEmail: function () { return submittedEmail; } };
  }

  window.AldoNewsletter = {
    submit: submit,
    isSubscribed: isSubscribed,
    markSubscribed: markSubscribed,
    createStepper: createStepper,
  };
})();
